import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import SEPlayEditDialogListItem from './components/SEPlayEditDialogListItem';
import SEPlayEditDialogAddButton from './components/SEPlayEditDialogAddButton';
import { DialogImage, SEPlayDialogLabels } from './SEPlayTypes';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import SortableList, { SortableItem } from 'react-easy-sort';

type SEPlayDialogProps = {
    openEditSlideshow: boolean;
    onClose: () => void;
    imageList: DialogImage[];
    labels: SEPlayDialogLabels;
    handleAddImagesView: () => void;
    handleRemovedImages: (image) => void;
    onSubmit: () => void;
    onDrop: (dragItem: number, dragOverItem: number) => void;
};

export const SEPlayEditDialog = (props: SEPlayDialogProps) => {
    let {
        openEditSlideshow,
        onClose,
        imageList,
        labels,
        handleAddImagesView,
        handleRemovedImages,
        onSubmit,
        onDrop
    } = props;

    const [removeImage, setRemoveImage] = React.useState([]);

    const handleRemoveImage = (image) => {
        let imageItem = {
            reference: image.reference,
            includedInSlideshow: image.includedInSlideshow
        };
        setRemoveImage((prev) => {
            return prev.concat([imageItem]);
        });
        handleRemovedImages(image);
    };

    function checkCurrentImageIndex(image) {
        return image.includedInSlideshow === true;
    }

    let currentImagesIndex = imageList.filter(checkCurrentImageIndex);

    const handleSubmit = () => {
        onSubmit();
        onClose();
    };

    const handleSortEnd = (oldIndex: number, newIndex: number) => {
        const dragItem = currentImagesIndex[oldIndex].orderInSlideshow;
        const dragOverItem = currentImagesIndex[newIndex].orderInSlideshow;
        onDrop(dragItem, dragOverItem);
    };

    const SaveButton = ({ label, onSubmit }) => {
        return (
            <Button
                aria-label="save-button"
                variant="contained"
                sx={{ borderRadius: '4px', fontSize: '13px', justifyItems: 'flex-end' }}
                onClick={onSubmit}
            >
                {label}
            </Button>
        );
    };

    return (
        <Dialog
            aria-label="slideshow-dialog"
            fullWidth={true}
            maxWidth="xl"
            margin={0}
            open={openEditSlideshow}
            onClose={onClose}
        >
            <DialogContent sx={{ height: '90vh' }}>
                <Toolbar disableGutters={true} sx={{ paddingLeft: 0, paddingRight: 0, margin: 0 }}>
                    <DialogTitle variant="h4" sx={{ flex: 1, ml: 2, padding: 0, margin: 0 }}>
                        {labels.dialogTitle}
                    </DialogTitle>
                    <SaveButton label={labels.dialogSaveButton} onSubmit={handleSubmit} />
                </Toolbar>

                <Grid
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        maxWidth: '100%',
                        justifyContent: 'center',
                        border: '2px dashed rgba(0, 0, 0, 0.12)',
                        borderRadius: ' 4px'
                    }}>
                    <SortableList onSortEnd={handleSortEnd}
                        className="sortable-list" draggedItemClassName="sortable-dragged"
                        aria-label='image-list' role='list'>
                        {imageList.length > 0
                            ? imageList.map((image, index) => {
                                let listNumber = currentImagesIndex.indexOf(image) + 1;
                                if (image.includedInSlideshow === true) {
                                    return (
                                        <SortableItem key={index}>
                                            <Box
                                                display={'flex'}
                                                key={index}
                                                sx={{ paddingBottom: '16px' }}
                                            >
                                                <SEPlayEditDialogListItem
                                                    tooltipLabel={labels.removeImageTooltip}
                                                    dragTooltip={labels.dragTooltip}
                                                    key={index}
                                                    image={image}
                                                    title={listNumber}
                                                    handleUpdateImage={() => handleRemoveImage(image)}
                                                />
                                            </Box>
                                        </SortableItem>
                                    );
                                }
                            })
                            : null}
                        {imageList.length != currentImagesIndex.length ? (
                            <SEPlayEditDialogAddButton
                                handleAddImagesView={handleAddImagesView}
                                label={labels.addButtonLabel}
                            />
                        ) : null}
                    </SortableList>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default SEPlayEditDialog;
